class CartGiftKits extends HTMLElement {
  // Accordion state per context (drawer, cart page). The element is recreated each time
  // its section is re-rendered, so the state is kept on the class.
  static openStates = {};

  constructor() {
    super();

    this.context = this.dataset.context;
    this.details = this.querySelector('details');
    this.errorMessage = this.querySelector('.cart-gift-kits__error');

    if (CartGiftKits.openStates[this.context] !== undefined) {
      this.details.open = CartGiftKits.openStates[this.context];
    }

    this.details.addEventListener('toggle', () => {
      CartGiftKits.openStates[this.context] = this.details.open;
    });
    this.addEventListener('change', this.onChange.bind(this));
  }

  async onChange(event) {
    const input = event.target;
    if (input.type !== 'checkbox') return;

    // Another mutation is pending (see CartItems.lock): cancel this toggle.
    if (CartItems.isMutating) {
      input.checked = !input.checked;
      return;
    }

    // Renders the drawer or the cart page that holds this element.
    const cartItems = this.closest('#main-cart, #mini-cart')?.querySelector('cart-items')
      || document.querySelector('cart-items');
    if (!cartItems) {
      input.checked = !input.checked;
      this.showError();
      return;
    }

    // Looked up before the re-render, which detaches this element.
    const drawer = this.closest('cart-drawer');
    const sections = cartItems.getSectionsToRender().map((section) => section.section);
    const body = JSON.stringify({
      updates: { [input.value]: input.checked ? 1 : 0 },
      sections: sections,
      sections_url: window.location.pathname
    });

    CartItems.lock();
    this.toggleLoading(input, true);

    try {
      const response = await fetch(window.routes.cart_update_url, { ...fetchConfig(), ...{ body } });
      const state = await response.text();
      const parsedState = JSON.parse(state);
      if (!response.ok || !parsedState.sections) throw new Error(parsedState.description || response.statusText);

      cartItems.renderSections(parsedState);
      // Kits count as cart items: keep the cached count in sync, or the next quantity change
      // that lands on the stale value is wrongly reported as an inventory limit.
      cartItems.currentItemCount = parsedState.item_count;

      // The re-render replaced the checkbox: give focus back to the new one. In the header drawer,
      // trapFocus() holds the first/last focusable nodes captured at opening, which are now
      // detached: set the trap up again so Tab and Shift+Tab keep wrapping inside the drawer.
      const newInput = document.getElementById(input.id);
      const drawerToggle = drawer?.mainDetailsToggle;
      if (newInput && drawerToggle?.hasAttribute('open')) {
        trapFocus(drawerToggle, newInput);
      } else {
        newInput?.focus();
      }

      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: {
          cart: state
        }
      }));
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-gift-kits' });
    } catch (error) {
      console.error(error);
      CartItems.unlock();
      input.checked = !input.checked;
      this.toggleLoading(input, false);
      input.focus();
      this.showError();
      return;
    }

    CartItems.unlock();
    if (this.isConnected) this.toggleLoading(input, false);
  }

  showError() {
    this.errorMessage.textContent = window.cartStrings.error;
    this.errorMessage.classList.remove('hidden');
  }

  toggleLoading(input, isLoading) {
    this.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.disabled = isLoading;
    });
    input.closest('.cart-gift-kit').querySelector('.loading-overlay').classList.toggle('hidden', !isLoading);
    if (isLoading) this.errorMessage.classList.add('hidden');
  }
}
customElements.define('cart-gift-kits', CartGiftKits);
